// src/components/DocumentCategoriesManager.js
// Direct port of complaints/ComplaintCategoriesManager.js — rename/reorder/activate-deactivate
// a company's Document Center tabs. No delete: deactivating hides a tab without ever orphaning
// the documents already filed under it (see docs/DOCUMENTS_MODULE.md, "Custom Tabs").
import React, { useEffect, useMemo, useState } from 'react';
import documentCategoryService from '../services/documentCategoryService';

export default function DocumentCategoriesManager({ onChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const [form, setForm] = useState({ name: '' });

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', isActive: true });

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const activeCount = useMemo(
    () => categories.filter(c => c.isActive).length,
    [categories]
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await documentCategoryService.getCategories();
      setCategories(data || []);
    } catch (e) {
      console.error(e);
      setMsg('Error loading tabs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toast = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 2500);
  };

  const notifyChange = async () => {
    await load();
    onChange?.();
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast('Tab name is required.');

    try {
      const maxOrder = categories.reduce((m, c) => Math.max(m, c.sortOrder || 0), 0);
      await documentCategoryService.createCategory({
        name: form.name.trim(),
        sortOrder: maxOrder + 10,
      });
      setForm({ name: '' });
      toast('Tab created.');
      await notifyChange();
    } catch (e) {
      console.error(e);
      toast('Error creating tab.');
    }
  };

  const beginEdit = (cat) => {
    setEditId(cat.id);
    setEditForm({
      name: cat.name || '',
      isActive: !!cat.isActive,
    });
  };

  const cancelEdit = () => setEditId(null);

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast('Tab name is required.');
    try {
      await documentCategoryService.updateCategory(editId, {
        name: editForm.name.trim(),
        isActive: !!editForm.isActive,
      });
      toast('Tab updated.');
      setEditId(null);
      await notifyChange();
    } catch (e) {
      console.error(e);
      toast('Error updating tab.');
    }
  };

  // ── Drag reordering ────────────────────────────────────────────────────────
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragLeave = () => setDragOverIdx(null);
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const reordered = [...categories];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setDragIdx(null);
    setCategories(reordered);
    try {
      await documentCategoryService.reorderCategories(reordered.map((c, i) => ({ id: c.id, sortOrder: i * 10 })));
      onChange?.();
    } catch {
      toast('Error saving tab order.');
      await load();
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-muted" style={{ fontSize: 13 }}>
          {categories.length} total &bull; {activeCount} active
        </div>
        <button className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {msg && <div className="alert alert-info mt-3 py-2">{msg}</div>}

      {/* Create */}
      <form onSubmit={onCreate} className="mt-3 row g-2 align-items-end">
        <div className="col">
          <label className="form-label mb-1">New tab name</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. N51 / Regulatory Seals"
            required
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" type="submit">Add Tab</button>
        </div>
      </form>

      {/* List */}
      <hr />
      <div className="text-muted mb-1" style={{ fontSize: 12 }}>Drag rows by the ⠿ handle to reorder tabs.</div>
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Name</th>
              <th style={{ width: 90 }}>Documents</th>
              <th style={{ width: 90 }}>Active</th>
              <th style={{ width: 150 }}></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, idx) => (
              <tr
                key={cat.id}
                draggable={editId === null}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, idx)}
                style={{
                  opacity: dragIdx === idx ? 0.4 : 1,
                  borderTop: dragOverIdx === idx ? '2px solid #0d6efd' : undefined,
                  cursor: editId === null ? 'grab' : 'default',
                }}
              >
                <td className="text-muted text-center" style={{ fontSize: 14 }}>⠿</td>
                <td>
                  {editId === cat.id ? (
                    <input
                      className="form-control form-control-sm"
                      value={editForm.name}
                      onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                    />
                  ) : (
                    <span style={{ fontWeight: 600 }}>{cat.name}</span>
                  )}
                </td>

                <td className="text-muted">{cat.documentCount ?? 0}</td>

                <td>
                  {editId === cat.id ? (
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm(f => ({ ...f, isActive: e.target.checked }))}
                    />
                  ) : (
                    <span className={`badge ${cat.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {cat.isActive ? 'Yes' : 'No'}
                    </span>
                  )}
                </td>

                <td className="text-end">
                  {editId === cat.id ? (
                    <div className="btn-group">
                      <button className="btn btn-sm btn-primary" type="button" onClick={saveEdit}>
                        Save
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => beginEdit(cat)}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {categories.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted">No tabs yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
